import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
import { api, fmtBRL, fmtPct, MESES, mesAnoLabel, ordenarPorQuarto } from './utils/api';

const isVago = (nome) => String(nome||'').trim().toUpperCase() === 'VAGO';
const round2 = (v) => Math.round((parseFloat(v) || 0) * 100) / 100;

// ─── Toast context ────────────────────────────────────────────────────────────
const ToastCtx = createContext(null);
function useToast() { return useContext(ToastCtx); }

function ToastProvider({ children }) {
  const [toast, setToast] = useState(null);
  const show = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </ToastCtx.Provider>
  );
}

// ─── Auth ─────────────────────────────────────────────────────────────────────
function useAuth() {
  const token = localStorage.getItem('pen_token');
  const nome  = localStorage.getItem('pen_nome');
  return { logado: !!token, nome };
}

function ProtectedRoute({ children }) {
  const { logado } = useAuth();
  return logado ? children : <Navigate to="/login" replace />;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────
function useMesAno() {
  const now = new Date();
  const [mes, setMes] = useState(now.getMonth() + 1);
  const [ano, setAno] = useState(now.getFullYear());
  return { mes, ano, setMes, setAno };
}

// ─── Shared Components ────────────────────────────────────────────────────────
function MesPicker({ mes, ano, onChange }) {
  const now  = new Date();
  const opts = [];
  for (let i = -3; i <= 12; i++) {
    let m = now.getMonth() + 1 + i, y = now.getFullYear();
    while (m < 1) { m += 12; y--; }
    while (m > 12) { m -= 12; y++; }
    opts.push({ m, y, label: MESES[m - 1] + '/' + String(y).slice(2) });
  }

  const activeRef = useRef(null);

  useEffect(() => {
    if (activeRef.current) {
      activeRef.current.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [mes, ano]);

  return (
    <div className="mes-picker">
      {opts.map(o => {
        const isActive = o.m === mes && o.y === ano;
        return (
          <button key={o.m + '-' + o.y}
            ref={isActive ? activeRef : null}
            className={'mes-chip' + (isActive ? ' active' : '')}
            onClick={() => onChange(o.m, o.y)}>
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function Modal({ title, onClose, children }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <button className="modal-close" onClick={onClose}>✕</button>
        <div className="modal-title">{title}</div>
        {children}
      </div>
    </div>
  );
}

function Spinner() { return <div className="spinner" />; }

function TopBar({ title, sub, right, onBack }) {
  const nav = useNavigate();
  return (
    <div className="topbar">
      {onBack && <button className="topbar-btn" onClick={() => nav(-1)}>‹</button>}
      <div className="flex-1">
        <div className="topbar-title">{title}</div>
        {sub && <div className="topbar-sub">{sub}</div>}
      </div>
      {right}
    </div>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function Login() {
  const [form, setForm]   = useState({ login: '', senha: '' });
  const [load, setLoad]   = useState(false);
  const [err,  setErr]    = useState('');
  const nav = useNavigate();

  async function submit(e) {
    e.preventDefault(); setErr(''); setLoad(true);
    try {
      const r = await api.login(form.login, form.senha);
      localStorage.setItem('pen_token', r.token);
      localStorage.setItem('pen_nome',  r.nome);
      nav('/', { replace: true });
    } catch (e) { setErr(e.message); }
    finally { setLoad(false); }
  }

  return (
    <div className="login-page">
      <div className="login-logo">🏠</div>
      <div className="login-title">Pensionatos</div>
      <div className="login-sub">Jundiaí — Pilo & Beiço</div>
      <form className="login-card" onSubmit={submit}>
        <div className="form-group">
          <label className="form-label">Login</label>
          <input className="form-input" autoCapitalize="none" autoCorrect="off"
            value={form.login} onChange={e => setForm(f => ({...f, login: e.target.value}))} />
        </div>
        <div className="form-group">
          <label className="form-label">Senha</label>
          <input className="form-input" type="password"
            value={form.senha} onChange={e => setForm(f => ({...f, senha: e.target.value}))} />
        </div>
        {err && <p style={{color:'var(--red)',fontSize:13,marginBottom:12}}>{err}</p>}
        <button className="btn btn-primary" disabled={load}>
          {load ? <Spinner /> : 'Entrar'}
        </button>
      </form>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
function Dashboard() {
  const { mes, ano, setMes, setAno } = useMesAno();
  const [resumo, setResumo] = useState(null);
  const [load,   setLoad]   = useState(true);
  const { nome } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setLoad(true);
    api.resumo(mes, ano).then(setResumo).catch(console.error).finally(() => setLoad(false));
  }, [mes, ano]);

  const pctReceber = resumo ? Math.min((resumo.aReceberPct||0) * 100, 100) : 0;
  const pctInadim  = resumo ? Math.min((resumo.inadimplenciaPct||0) * 100, 100) : 0;

  return (
    <div>
      <TopBar title="🏠 Pensionatos" sub={`Olá, ${nome}`}
        right={<button className="topbar-btn" title="Sair"
                 onClick={() => { localStorage.clear(); window.location.href='/login'; }}>⎋</button>} />
      <div className="content">
        <MesPicker mes={mes} ano={ano} onChange={(m,a) => { setMes(m); setAno(a); }} />

        {load ? (
          <div className="empty-state"><div className="icon"><Spinner /></div></div>
        ) : resumo ? (<>
          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-label">Total Bruto</div>
              <div className="stat-value">{fmtBRL(resumo.totalBruto)}</div>
              <div className="stat-sub">projetado</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Total Bruto Recebido</div>
              <div className="stat-value green">{fmtBRL(resumo.totalRecebido)}</div>
              <div className="stat-sub">confirmado</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Liq. Projetado</div>
              <div className="stat-value amber">{fmtBRL(resumo.liquidoProjetado)}</div>
              <div className="stat-sub">bruto − custos</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Liq. Recebido</div>
              <div className="stat-value blue">{fmtBRL(resumo.liquidoRecebido)}</div>
              <div className="stat-sub">real no mês</div>
            </div>
          </div>

          <div className="card">
            <div className="flex justify-between items-center mb-8">
              <div className="card-title" style={{marginBottom:0}}>💰 Saldo em Caixa</div>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <div style={{fontSize:11,color:'var(--text-lt)'}}>Saldo inicial do mês</div>
                <div style={{fontSize:16,fontWeight:700}}>{fmtBRL(resumo.saldoInicial)}</div>
              </div>
              <span style={{fontSize:20,color:'var(--text-lt)'}}>→</span>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:11,color:'var(--text-lt)'}}>Saldo final do mês</div>
                <div className={'stat-value ' + (resumo.saldoFinal>=0?'green':'red')} style={{fontSize:16}}>
                  {fmtBRL(resumo.saldoFinal)}
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex justify-between items-center mb-8">
              <div className="card-title" style={{marginBottom:0}}>À Receber</div>
              <span className="badge badge-amber">{fmtPct(resumo.aReceberPct)}</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill amber" style={{width: pctReceber+'%'}} />
            </div>
            <div style={{fontSize:12,color:'var(--text-lt)',marginTop:4}}>
              {fmtBRL(resumo.aReceber)} ainda não recebido neste mês
            </div>
          </div>

          <div className="card">
            <div className="flex justify-between items-center mb-8">
              <div className="card-title" style={{marginBottom:0}}>Inadimplência</div>
              <span className="badge badge-red">{fmtPct(resumo.inadimplenciaPct)}</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill red" style={{width: pctInadim+'%'}} />
            </div>
            <div style={{fontSize:12,color:'var(--text-lt)',marginTop:4}}>
              {fmtBRL(resumo.totalInadimplencia)} vencido e não pago
            </div>
            {(resumo.inadimplentes||[]).length > 0 && (
              <div style={{marginTop:12,borderTop:'1px solid #f0f0f0',paddingTop:10}}>
                {resumo.inadimplentes.map((inad, i) => (
                  <div key={i} className="flex justify-between items-center" style={{marginBottom:8}}>
                    <div style={{minWidth:0,flex:1}}>
                      <div style={{fontSize:13,fontWeight:600}}>{inad.nome}</div>
                      <div style={{fontSize:11,color:'var(--text-lt)'}}>
                        {inad.imovel_nome}{inad.quarto ? ' · ' + inad.quarto : ''}
                        {inad.status === 'PARCIAL' ? ` · pagou ${fmtBRL(inad.pago)} de ${fmtBRL(inad.devido)}` : ''}
                      </div>
                    </div>
                    <span style={{fontWeight:700,color:'var(--red)',fontSize:14}}>{fmtBRL(inad.saldo)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-title">Custos Fixos do Mês</div>
            <div className="flex justify-between items-center">
              <div>
                <div style={{fontSize:11,color:'var(--text-lt)'}}>Pago</div>
                <div style={{fontSize:16,fontWeight:700,color:'var(--green-med)'}}>{fmtBRL(resumo.custosPago)}</div>
              </div>
              <div style={{textAlign:'right'}}>
                <div style={{fontSize:11,color:'var(--text-lt)'}}>Falta pagar</div>
                <div style={{fontSize:16,fontWeight:700,color:'var(--red)'}}>{fmtBRL(resumo.custosPendente)}</div>
              </div>
            </div>
            <div className="progress-bar" style={{marginTop:8}}>
              <div className="progress-fill"
                style={{width: resumo.totalCustos>0 ? (resumo.custosPago/resumo.totalCustos*100)+'%' : '0%'}} />
            </div>
          </div>

          <div className="card">
            <div className="card-title">Por Imóvel</div>
            {(resumo.porImovel||[]).map(im => (
              <div key={im.id} style={{cursor:'pointer',marginBottom:14}}
                   onClick={() => nav(`/imovel/${im.id}?mes=${mes}&ano=${ano}`)}>
                <div className="flex justify-between items-center">
                  <span style={{fontWeight:600,fontSize:14}}>
                    {im.nome}
                    {im.qtd_vago > 0 && (
                      <span className="badge badge-red" style={{marginLeft:6}}>
                        {im.qtd_vago} vago{im.qtd_vago>1?'s':''}
                      </span>
                    )}
                  </span>
                  <span style={{fontSize:13,color:'var(--green-med)',fontWeight:700}}>{fmtBRL(im.recebido)}</span>
                </div>
                <div className="progress-bar" style={{marginTop:4}}>
                  <div className="progress-fill"
                    style={{width: im.bruto > 0 ? (im.recebido/im.bruto*100)+'%' : '0%'}} />
                </div>
                <div style={{fontSize:11,color:'var(--text-lt)'}}>
                  {fmtBRL(im.bruto)} projetado · custo fixo {fmtBRL(im.custo_fixo)}
                </div>
              </div>
            ))}
          </div>

          <div className="stat-grid">
            <div className="stat-card">
              <div className="stat-label">Custos Fixos</div>
              <div className="stat-value red">{fmtBRL(resumo.totalCustos)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Gastos Variáveis</div>
              <div className="stat-value red">{fmtBRL(resumo.totalGastos)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Reocupações</div>
              <div className="stat-value amber">{fmtBRL(resumo.reocupacoes)}</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Quartos Vagos</div>
              <div className={'stat-value ' + (resumo.totalVagos > 0 ? 'red' : 'green')}>{resumo.totalVagos}</div>
              <div className="stat-sub">de {resumo.qtdQuartos} quartos</div>
            </div>
          </div>
        </>) : null}
      </div>
    </div>
  );
}

// ─── IMÓVEL DETAIL ────────────────────────────────────────────────────────────
function ImovelDetalhe() {
  const loc = useLocation();
  const id   = parseInt(window.location.pathname.split('/')[2]);
  const params = new URLSearchParams(loc.search);
  const now  = new Date();
  const [mes, setMes] = useState(parseInt(params.get('mes')) || now.getMonth()+1);
  const [ano, setAno] = useState(parseInt(params.get('ano')) || now.getFullYear());
  const [imoveis, setImoveis] = useState([]);
  const [pags,    setPags]    = useState([]);
  const [load,    setLoad]    = useState(true);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalEdit, setModalEdit] = useState(null);
  const [modalPg,   setModalPg]   = useState(null);
  const toast = useToast();

  const imovel = imoveis.find(i => i.id === id);

  useEffect(() => { api.imoveis().then(setImoveis).catch(console.error); }, []);

  const carregar = useCallback(() => {
    setLoad(true);
    api.pagamentos(id, mes, ano)
      .then(data => setPags(ordenarPorQuarto(data)))
      .catch(console.error)
      .finally(() => setLoad(false));
  }, [id, mes, ano]);
  useEffect(() => { carregar(); }, [carregar]);

  function pgInfo(h) {
    const status = h.status;
    if (!status || status === '') return { cls:'pg-vazio', label:'—' };
    if (status === 'PG')      return { cls:'pg-pago', label:'PG' };
    if (status === 'PARCIAL') return { cls:'pg-parcial', label: fmtBRL(h.valor_recebido).replace('R$','') };
    return { cls:'pg-nao', label:'X' };
  }

  const totalBruto = pags.reduce((s, h) => s + Number(h.valor_cobrado ?? h.valor), 0);
  const totalPago  = pags.reduce((s, h) => {
    if (h.status === 'PG')      return s + Number(h.valor_recebido ?? h.valor_cobrado ?? h.valor);
    if (h.status === 'PARCIAL') return s + Number(h.valor_recebido || 0);
    return s;
  }, 0);

  return (
    <div>
      <TopBar title={imovel?.nome || '...'} sub={mesAnoLabel(mes, ano)} onBack />
      <div className="content">
        <MesPicker mes={mes} ano={ano} onChange={(m,a)=>{setMes(m);setAno(a);}} />

        <div className="stat-grid" style={{gridTemplateColumns:'1fr 1fr 1fr'}}>
          <div className="stat-card">
            <div className="stat-label">Bruto</div>
            <div className="stat-value" style={{fontSize:15}}>{fmtBRL(totalBruto)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Recebido</div>
            <div className="stat-value green" style={{fontSize:15}}>{fmtBRL(totalPago)}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Em aberto</div>
            <div className="stat-value red" style={{fontSize:15}}>{fmtBRL(totalBruto-totalPago)}</div>
          </div>
        </div>

        <div className="card">
          <div className="flex justify-between items-center mb-8">
            <div className="card-title" style={{marginBottom:0}}>Hóspedes</div>
            <button className="btn btn-primary btn-sm" onClick={() => setModalNovo(true)}>+ Novo</button>
          </div>
          <div style={{fontSize:11,color:'var(--text-lt)',marginBottom:8}}>
            Toque no nome para editar · toque no botão de status para lançar pagamento
          </div>
          {load ? <div className="empty-state"><Spinner /></div>
          : pags.length === 0 ? <div className="empty-state"><div className="icon">🛏</div>Nenhum hóspede</div>
          : pags.map(h => {
            const vago = isVago(h.nome);
            const pg = pgInfo(h);
            return (
              <div key={h.hospede_id} className={'hospede-row' + (vago ? ' vago' : '')}>
                <div className="hospede-info" style={{cursor:'pointer'}} onClick={() => setModalEdit(h)}>
                  <div className="hospede-nome">
                    {h.quarto ? <span style={{color: vago ? 'var(--red)' : 'var(--green-med)'}}>{h.quarto} — </span> : ''}
                    {vago ? 'QUARTO VAGO' : h.nome} <span style={{color:'var(--text-lt)',fontWeight:400}}>✎</span>
                  </div>
                  <div className="hospede-sub">
                    {vago ? 'Toque para atribuir um hóspede' : `${fmtBRL(h.valor_cobrado ?? h.valor)} · dia ${h.dia_pg}`}
                  </div>
                </div>
                {vago
                  ? <span className="badge-vago">VAGO</span>
                  : <button className={'pg-btn ' + pg.cls} onClick={() => setModalPg(h)}>{pg.label}</button>}
              </div>
            );
          })}
        </div>

        <CustosFixos imovelId={id} mes={mes} ano={ano} />
      </div>

      {modalNovo && <ModalNovoHospede imovelId={id} onClose={() => setModalNovo(false)}
        onSaved={() => { setModalNovo(false); carregar(); }} />}

      {modalEdit && <ModalEditarHospede hospede={modalEdit} onClose={() => setModalEdit(null)}
        onSaved={() => { setModalEdit(null); carregar(); }} />}

      {modalPg && <ModalPagamento hospede={modalPg} mes={mes} ano={ano} onClose={() => setModalPg(null)}
        onSaved={() => { setModalPg(null); carregar(); }} />}
    </div>
  );
}

// ─── PAGAMENTO (total, parcial, desconto) ─────────────────────────────────────
function ModalPagamento({ hospede, mes, ano, onClose, onSaved }) {
  const valorPadrao = Number(hospede.valor) || 0;
  const devidoInicial = hospede.valor_cobrado != null ? hospede.valor_cobrado : valorPadrao;
  const recebidoInicial = hospede.valor_recebido != null ? hospede.valor_recebido
    : (hospede.status === 'PG' ? devidoInicial : '');

  const [devido,   setDevido]   = useState(String(devidoInicial));
  const [recebido, setRecebido] = useState(String(recebidoInicial));
  const [load, setLoad] = useState(false);
  const toast = useToast();

  async function salvarComStatus(statusForcado, recebidoVal) {
    setLoad(true);
    try {
      const d = round2(devido);
      const r = recebidoVal !== undefined ? round2(recebidoVal) : round2(recebido);
      let status = statusForcado;
      if (!status) {
        if (r <= 0) status = 'X';
        else if (r >= d) status = 'PG';
        else status = 'PARCIAL';
      }
      await api.marcarPg({
        hospede_id: hospede.hospede_id, mes, ano, status,
        valor_cobrado: d, valor_recebido: r,
      });
      const msg = status === 'PG' ? `✅ ${hospede.nome} — pago!`
        : status === 'PARCIAL' ? `🔶 ${hospede.nome} — parcial registrado`
        : `❌ ${hospede.nome} — marcado como não pago`;
      toast(msg);
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  async function limpar() {
    setLoad(true);
    try {
      await api.marcarPg({ hospede_id: hospede.hospede_id, mes, ano, status: '', valor_cobrado: null, valor_recebido: null });
      toast('Lançamento desfeito');
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  function salvar(e) {
    e.preventDefault();
    salvarComStatus(null);
  }

  return (
    <Modal title={`Pagamento — ${hospede.nome}`} onClose={onClose}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Valor devido este mês (R$)</label>
          <input className="form-input" type="number" step="0.01" value={devido}
            onChange={e=>setDevido(e.target.value)} required />
          <div className="form-hint">
            Altere para aplicar desconto só neste mês. Valor padrão do quarto: {fmtBRL(valorPadrao)}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Valor recebido (R$)</label>
          <input className="form-input" type="number" step="0.01" value={recebido}
            onChange={e=>setRecebido(e.target.value)} required />
          <div className="form-hint">
            Se for menor que o valor devido, fica registrado como pagamento parcial — o restante continua a cobrar.
          </div>
        </div>

        <div className="flex gap-8" style={{marginBottom:14}}>
          <button type="button" className="btn btn-outline btn-sm"
            onClick={() => setRecebido(devido)}>Preencher Total</button>
          <button type="button" className="btn btn-outline btn-sm"
            onClick={() => setRecebido('0')}>Não Recebi Nada</button>
        </div>

        <button className="btn btn-primary" disabled={load}>{load?<Spinner/>:'Salvar Pagamento'}</button>
        <div style={{height:10}} />
        <button type="button" className="btn btn-outline" disabled={load} onClick={limpar}>
          Desfazer Lançamento deste Mês
        </button>
      </form>
    </Modal>
  );
}

function CustosFixos({ imovelId, mes, ano }) {
  const [custos, setCustos] = useState([]);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalEdit, setModalEdit] = useState(null);
  const toast = useToast();

  const carregar = useCallback(() =>
    api.custos(imovelId, mes, ano).then(setCustos).catch(console.error), [imovelId, mes, ano]);
  useEffect(() => { carregar(); }, [carregar]);

  const total   = custos.reduce((s,c) => s + Number(c.valor), 0);
  const pago    = custos.reduce((s,c) => s + (c.pago ? Number(c.valor_pago ?? c.valor) : 0), 0);
  const pendente = total - pago;

  async function togglePago(c) {
    const novoPago = !c.pago;
    setCustos(cs => cs.map(x => x.id === c.id ? {...x, pago: novoPago} : x));
    try {
      await api.marcarCustoPago(c.id, { mes, ano, pago: novoPago, valor_pago: c.valor });
      toast(novoPago ? `✅ ${c.descricao} marcado como pago` : `↩ ${c.descricao} marcado como pendente`);
    } catch(e) {
      carregar();
      toast(e.message, 'error');
    }
  }

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-8">
        <div className="card-title" style={{marginBottom:0}}>Custos Fixos</div>
        <span style={{fontWeight:700,color:'var(--red)',fontSize:14}}>{fmtBRL(total)}</span>
      </div>
      <div className="flex justify-between" style={{marginBottom:10}}>
        <span style={{fontSize:12,color:'var(--green-med)',fontWeight:600}}>Pago: {fmtBRL(pago)}</span>
        <span style={{fontSize:12,color:'var(--red)',fontWeight:600}}>Falta: {fmtBRL(pendente)}</span>
      </div>
      <div style={{fontSize:11,color:'var(--text-lt)',marginBottom:8}}>
        Toque no nome para editar · marque a caixa quando pagar
      </div>

      {custos.length === 0
        ? <div className="empty-state" style={{padding:'16px 0'}}>Nenhum custo fixo cadastrado</div>
        : custos.map(c => (
          <div key={c.id} className="hospede-row">
            <input type="checkbox" checked={!!c.pago} onChange={() => togglePago(c)}
              style={{width:20,height:20,accentColor:'var(--green-med)',flexShrink:0}} />
            <div className="hospede-info" style={{cursor:'pointer'}} onClick={() => setModalEdit(c)}>
              <div className="hospede-nome" style={{fontSize:13, textDecoration: c.pago ? 'line-through' : 'none', color: c.pago ? 'var(--text-lt)' : undefined}}>
                {c.descricao} <span style={{color:'var(--text-lt)',fontWeight:400}}>✎</span>
              </div>
              {c.dia_venc && <div className="hospede-sub">Venc. dia {c.dia_venc}</div>}
            </div>
            <span style={{fontWeight:700,fontSize:14, color: c.pago ? 'var(--green-med)' : undefined}}>{fmtBRL(c.valor)}</span>
          </div>
        ))}

      <div style={{marginTop:12}}>
        <button className="btn btn-outline btn-sm" onClick={() => setModalNovo(true)}>+ Lançar Custo Fixo</button>
      </div>

      {modalNovo && <ModalCusto titulo="Novo Custo Fixo" imovelId={imovelId} mes={mes} ano={ano}
        onClose={() => setModalNovo(false)}
        onSaved={() => { setModalNovo(false); carregar(); toast('Custo lançado!'); }} />}

      {modalEdit && <ModalCusto titulo="Editar Custo Fixo" custo={modalEdit} mes={mes} ano={ano}
        onClose={() => setModalEdit(null)}
        onSaved={() => { setModalEdit(null); carregar(); toast('Custo atualizado!'); }}
        onRemovido={() => { setModalEdit(null); carregar(); toast('Custo removido!'); }} />}
    </div>
  );
}

function ModalCusto({ titulo, imovelId, custo, mes, ano, onClose, onSaved, onRemovido }) {
  const [form, setForm] = useState({
    descricao: custo?.descricao || '',
    valor:     custo?.valor != null ? custo.valor.toFixed(2) : '',
    dia_venc:  custo?.dia_venc || '',
  });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      const valorNum = round2(form.valor);
      const body = { descricao: form.descricao, valor: valorNum, dia_venc: form.dia_venc || null, mes, ano };
      if (custo) await api.editarCusto(custo.id, body);
      else       await api.criarCusto({ ...body, imovel_id: imovelId });
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  async function remover() {
    if (!window.confirm(`Remover o custo "${form.descricao}"? Esta ação não pode ser desfeita pelo app.`)) return;
    setLoad(true);
    try { await api.excluirCusto(custo.id); onRemovido(); }
    catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title={titulo} onClose={onClose}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Descrição</label>
          <input className="form-input" value={form.descricao} onChange={e=>f('descricao',e.target.value)} required />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Valor deste mês (R$)</label>
            <input className="form-input" type="number" step="0.01" value={form.valor} onChange={e=>f('valor',e.target.value)} required />
            <div className="form-hint">Editar aqui não altera outros meses (ideal para água, luz, etc. que variam)</div>
          </div>
          <div className="form-group">
            <label className="form-label">Dia Venc.</label>
            <input className="form-input" value={form.dia_venc} onChange={e=>f('dia_venc',e.target.value)} placeholder="ex: 15" />
          </div>
        </div>
        <button className="btn btn-primary" disabled={load}>{load?<Spinner/>:'Salvar'}</button>
        {custo && (<>
          <div style={{height:10}} />
          <button type="button" className="btn btn-danger" disabled={load} onClick={remover}>
            Remover Custo Fixo
          </button>
        </>)}
      </form>
    </Modal>
  );
}

function ModalNovoHospede({ imovelId, onClose, onSaved }) {
  const [form, setForm] = useState({ nome:'', quarto:'', dia_pg:'', valor:'' });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      await api.adicionarHospede({...form, imovel_id: imovelId, valor: round2(form.valor)});
      toast('Hóspede adicionado!'); onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title="Novo Hóspede" onClose={onClose}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Nome</label>
          <input className="form-input" value={form.nome} onChange={e=>f('nome',e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Quarto (opcional)</label>
          <input className="form-input" value={form.quarto} onChange={e=>f('quarto',e.target.value)} placeholder="ex: 9A" />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Dia PG</label>
            <input className="form-input" value={form.dia_pg} onChange={e=>f('dia_pg',e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Valor (R$)</label>
            <input className="form-input" type="number" value={form.valor} onChange={e=>f('valor',e.target.value)} required />
          </div>
        </div>
        <button className="btn btn-primary" disabled={load}>{load?<Spinner/>:'Salvar'}</button>
      </form>
    </Modal>
  );
}

// ─── EDITAR HÓSPEDE (nome, valor, dia PG, quarto) ─────────────────────────────
function ModalEditarHospede({ hospede, onClose, onSaved }) {
  const vagoAtual = isVago(hospede.nome);
  const [form, setForm] = useState({
    nome:   vagoAtual ? '' : (hospede.nome   || ''),
    quarto: hospede.quarto || '',
    dia_pg: vagoAtual ? '' : (hospede.dia_pg || ''),
    valor:  vagoAtual ? '' : String(hospede.valor || ''),
  });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      await api.editarHospede(hospede.hospede_id, {
        nome: form.nome, quarto: form.quarto, dia_pg: form.dia_pg,
        valor: round2(form.valor), ativo: 1,
      });
      toast('Hóspede atualizado!'); onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  async function marcarVago() {
    if (!window.confirm(`Marcar este quarto como VAGO? Nome, vencimento e valor serão zerados até você atribuir um novo hóspede.`)) return;
    setLoad(true);
    try {
      await api.editarHospede(hospede.hospede_id, {
        nome: 'VAGO', quarto: form.quarto, dia_pg: '0', valor: 0, ativo: 1,
      });
      toast('Quarto marcado como vago'); onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  async function excluirDefinitivo() {
    if (!window.confirm(`Excluir este quarto/hóspede DEFINITIVAMENTE? Todo o histórico de pagamentos será perdido. Esta ação NÃO pode ser desfeita.`)) return;
    if (!window.confirm(`Tem certeza mesmo? Esta é a última confirmação.`)) return;
    setLoad(true);
    try {
      await api.excluirHospedeDefinitivo(hospede.hospede_id);
      toast('Quarto excluído definitivamente'); onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title={vagoAtual ? 'Atribuir Hóspede ao Quarto' : 'Editar Hóspede'} onClose={onClose}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Nome</label>
          <input className="form-input" value={form.nome} onChange={e=>f('nome',e.target.value)}
            placeholder={vagoAtual ? 'Nome do novo hóspede' : ''} required />
        </div>
        <div className="form-group">
          <label className="form-label">Quarto (opcional)</label>
          <input className="form-input" value={form.quarto} onChange={e=>f('quarto',e.target.value)} placeholder="ex: 9A" />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Dia de Vencimento</label>
            <input className="form-input" value={form.dia_pg} onChange={e=>f('dia_pg',e.target.value)} placeholder="ex: 15 ou 15/30" />
          </div>
          <div className="form-group">
            <label className="form-label">Valor (R$)</label>
            <input className="form-input" type="number" step="0.01" value={form.valor} onChange={e=>f('valor',e.target.value)} required />
          </div>
        </div>
        <button className="btn btn-primary" disabled={load}>{load?<Spinner/>:'Salvar Alterações'}</button>
        {!vagoAtual && (<>
          <div style={{height:10}} />
          <button type="button" className="btn btn-danger" disabled={load} onClick={marcarVago}>
            Marcar Quarto como Vago
          </button>
        </>)}
        <div style={{height:10}} />
        <button type="button" className="btn btn-outline" disabled={load} onClick={excluirDefinitivo}
          style={{borderColor:'var(--red)', color:'var(--red)'}}>
          🗑 Excluir Quarto Definitivamente
        </button>
      </form>
    </Modal>
  );
}

// ─── IMÓVEIS LIST ─────────────────────────────────────────────────────────────
function Imoveis() {
  const [imoveis, setImoveis] = useState([]);
  const [modalNovo, setModalNovo] = useState(false);
  const [modalEdit, setModalEdit] = useState(null);
  const nav = useNavigate();
  const toast = useToast();
  const now = new Date();
  const mes = now.getMonth()+1, ano = now.getFullYear();

  const carregar = useCallback(() => api.imoveis().then(setImoveis).catch(console.error), []);
  useEffect(() => { carregar(); }, [carregar]);

  return (
    <div>
      <TopBar title="🏘 Imóveis" />
      <div className="content">
        <div className="flex justify-between items-center mb-8">
          <span style={{fontSize:13,color:'var(--text-lt)'}}>{imoveis.length} casas ativas</span>
          <button className="btn btn-primary btn-sm" onClick={() => setModalNovo(true)}>+ Nova Casa</button>
        </div>
        {imoveis.map(im => (
          <div key={im.id} className="card">
            <div className="flex justify-between items-center">
              <div className="flex-1" style={{cursor:'pointer',minWidth:0}}
                   onClick={() => nav(`/imovel/${im.id}?mes=${mes}&ano=${ano}`)}>
                <div style={{fontWeight:700,fontSize:16}}>{im.nome}</div>
                {im.endereco && <div style={{fontSize:12,color:'var(--text-lt)',marginTop:2}}>{im.endereco}</div>}
              </div>
              <div className="flex gap-8 items-center">
                <button className="btn btn-outline btn-sm" style={{padding:'6px 10px'}}
                  onClick={() => setModalEdit(im)}>✎</button>
                <span style={{fontSize:22,color:'var(--text-lt)'}}
                  onClick={() => nav(`/imovel/${im.id}?mes=${mes}&ano=${ano}`)}>›</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modalNovo && <ModalImovel titulo="Nova Casa" onClose={() => setModalNovo(false)}
        onSaved={() => { setModalNovo(false); carregar(); toast('Casa adicionada!'); }} />}

      {modalEdit && <ModalImovel titulo="Editar Casa" imovel={modalEdit} onClose={() => setModalEdit(null)}
        onSaved={() => { setModalEdit(null); carregar(); toast('Casa atualizada!'); }}
        onRemovido={() => { setModalEdit(null); carregar(); toast('Casa removida!'); }} />}
    </div>
  );
}

function ModalImovel({ titulo, imovel, onClose, onSaved, onRemovido }) {
  const [form, setForm] = useState({ nome: imovel?.nome || '', endereco: imovel?.endereco || '' });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      if (imovel) await api.editarImovel(imovel.id, form);
      else        await api.criarImovel(form);
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  async function remover() {
    if (!window.confirm(`Remover a casa "${imovel.nome}"? Os hóspedes e histórico financeiro ficam preservados, mas a casa some das listas.`)) return;
    setLoad(true);
    try {
      await api.excluirImovel(imovel.id);
      onRemovido();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title={titulo} onClose={onClose}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Nome da Casa</label>
          <input className="form-input" value={form.nome} onChange={e=>f('nome',e.target.value)} required />
        </div>
        <div className="form-group">
          <label className="form-label">Endereço (opcional)</label>
          <input className="form-input" value={form.endereco} onChange={e=>f('endereco',e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={load}>{load?<Spinner/>:'Salvar'}</button>
        {imovel && (<>
          <div style={{height:10}} />
          <button type="button" className="btn btn-danger" disabled={load} onClick={remover}>
            Remover Casa
          </button>
        </>)}
      </form>
    </Modal>
  );
}

// ─── GASTOS ───────────────────────────────────────────────────────────────────
const CATS = ['MANUTENÇÃO','COMPRAS / MATERIAL','LIMPEZA','MELHORIA','EQUIPAMENTO','SERVIÇO TERCEIRO','OUTRO'];

function Gastos() {
  const { mes, ano, setMes, setAno } = useMesAno();
  const [imoveis, setImoveis] = useState([]);
  const [gastos,  setGastos]  = useState([]);
  const [modal,   setModal]   = useState(false);
  const toast = useToast();

  useEffect(() => { api.imoveis().then(setImoveis); }, []);
  const load = useCallback(() => api.gastos(mes, ano).then(setGastos), [mes, ano]);
  useEffect(() => { load(); }, [load]);

  async function excluir(id) {
    if (!window.confirm('Excluir este lançamento?')) return;
    try { await api.excluirGasto(id); load(); toast('Excluído!'); }
    catch(e) { toast(e.message,'error'); }
  }

  const total = gastos.reduce((s,g) => s+Number(g.valor), 0);

  return (
    <div>
      <TopBar title="💸 Gastos Variáveis" />
      <div className="content">
        <MesPicker mes={mes} ano={ano} onChange={(m,a)=>{setMes(m);setAno(a);}} />
        <div className="flex justify-between items-center mb-8">
          <span style={{fontWeight:700,color:'var(--red)',fontSize:16}}>{fmtBRL(total)}</span>
          <button className="btn btn-primary btn-sm" onClick={()=>setModal(true)}>+ Lançar</button>
        </div>
        {gastos.length === 0
          ? <div className="empty-state"><div className="icon">💸</div>Nenhum gasto neste mês</div>
          : gastos.map(g => (
            <div key={g.id} className="card" style={{padding:'12px 16px'}}>
              <div className="flex justify-between items-center">
                <div className="flex-1" style={{minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:14}}>{g.descricao || g.categoria}</div>
                  <div style={{fontSize:12,color:'var(--text-lt)'}}>
                    {g.imovel_nome || 'Geral'} · {g.categoria}
                    {g.fornecedor ? ' · ' + g.fornecedor : ''}
                  </div>
                  <div style={{fontSize:11,color:'var(--text-lt)'}}>
                    {new Date(g.data).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <span style={{fontWeight:700,color:'var(--red)',fontSize:15}}>{fmtBRL(g.valor)}</span>
                  <button className="btn btn-danger btn-sm" style={{width:32,padding:'6px'}}
                    onClick={()=>excluir(g.id)}>✕</button>
                </div>
              </div>
            </div>
          ))}
      </div>
      {modal && <ModalNovoGasto imoveis={imoveis} mes={mes} ano={ano}
        onClose={()=>setModal(false)} onSaved={()=>{setModal(false);load();toast('Gasto lançado!');}} />}
    </div>
  );
}

function ModalNovoGasto({ imoveis, mes, ano, onClose, onSaved }) {
  const pad = n => String(n).padStart(2,'0');
  const dataDefault = `${ano}-${pad(mes)}-${pad(Math.min(new Date().getDate(), new Date(ano,mes,0).getDate()))}`;
  const [form, setForm] = useState({ imovel_id:'', data:dataDefault, categoria:CATS[0], descricao:'', fornecedor:'', valor:'' });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      await api.adicionarGasto({...form, imovel_id: form.imovel_id||null, valor: round2(form.valor)});
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title="Lançar Gasto" onClose={onClose}>
      <form onSubmit={salvar}>
        <div className="form-group">
          <label className="form-label">Imóvel</label>
          <select className="form-input" value={form.imovel_id} onChange={e=>f('imovel_id',e.target.value)}>
            <option value="">Geral</option>
            {imoveis.map(i=><option key={i.id} value={i.id}>{i.nome}</option>)}
          </select>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Data</label>
            <input className="form-input" type="date" value={form.data} onChange={e=>f('data',e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Valor (R$)</label>
            <input className="form-input" type="number" step="0.01" value={form.valor} onChange={e=>f('valor',e.target.value)} required />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Categoria</label>
          <select className="form-input" value={form.categoria} onChange={e=>f('categoria',e.target.value)}>
            {CATS.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Descrição</label>
          <input className="form-input" value={form.descricao} onChange={e=>f('descricao',e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Fornecedor / Obs.</label>
          <input className="form-input" value={form.fornecedor} onChange={e=>f('fornecedor',e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={load}>{load?<Spinner/>:'Salvar'}</button>
      </form>
    </Modal>
  );
}

// ─── CAIXA SÓCIOS ─────────────────────────────────────────────────────────────
function Caixa() {
  const { mes, ano, setMes, setAno } = useMesAno();
  const [imoveis, setImoveis] = useState([]);
  const [itens,   setItens]   = useState([]);
  const [modal,   setModal]   = useState(false);
  const toast = useToast();

  useEffect(() => { api.imoveis().then(setImoveis); }, []);
  const load = useCallback(() => api.caixa(mes, ano).then(setItens), [mes, ano]);
  useEffect(() => { load(); }, [load]);

  async function excluir(id) {
    if (!window.confirm('Excluir?')) return;
    try { await api.excluirCaixa(id); load(); toast('Excluído!'); }
    catch(e) { toast(e.message,'error'); }
  }

  const totPilo  = itens.filter(i=>i.socio==='PILO');
  const totBeico = itens.filter(i=>i.socio==='BEIÇO');
  function saldo(lista) {
    return lista.reduce((s,i) => s + (i.tipo==='RECEBIMENTO'?1:-1)*Number(i.valor),0);
  }

  return (
    <div>
      <TopBar title="💰 Caixa Sócios" />
      <div className="content">
        <MesPicker mes={mes} ano={ano} onChange={(m,a)=>{setMes(m);setAno(a);}} />

        <div className="stat-grid">
          <div className="stat-card">
            <div className="stat-label">Saldo Pilo</div>
            <div className={'stat-value '+(saldo(totPilo)>=0?'green':'red')}>{fmtBRL(saldo(totPilo))}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Saldo Beiço</div>
            <div className={'stat-value '+(saldo(totBeico)>=0?'green':'red')}>{fmtBRL(saldo(totBeico))}</div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-8">
          <span style={{fontWeight:700,fontSize:14,color:'var(--text-lt)'}}>Lançamentos</span>
          <button className="btn btn-blue btn-sm" onClick={()=>setModal(true)}>+ Lançar</button>
        </div>

        {itens.length === 0
          ? <div className="empty-state"><div className="icon">💰</div>Nenhum lançamento</div>
          : itens.map(it => (
            <div key={it.id} className="card" style={{padding:'12px 16px'}}>
              <div className="flex justify-between items-center">
                <div className="flex-1" style={{minWidth:0}}>
                  <div className="flex items-center gap-8">
                    <span className={'badge '+(it.tipo==='RECEBIMENTO'?'badge-green':'badge-red')}>{it.tipo}</span>
                    <span className="badge badge-blue">{it.socio}</span>
                  </div>
                  <div style={{fontWeight:600,fontSize:14,marginTop:4}}>{it.descricao||it.hospede_descricao||'—'}</div>
                  <div style={{fontSize:12,color:'var(--text-lt)'}}>
                    {it.imovel_nome||''} · {new Date(it.data).toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <span style={{fontWeight:700,fontSize:15,color:it.tipo==='RECEBIMENTO'?'var(--green-med)':'var(--red)'}}>
                    {it.tipo==='RECEBIMENTO'?'+':'-'}{fmtBRL(it.valor)}
                  </span>
                  <button className="btn btn-danger btn-sm" style={{width:32,padding:'6px'}}
                    onClick={()=>excluir(it.id)}>✕</button>
                </div>
              </div>
            </div>
          ))}
      </div>
      {modal && <ModalNovoCaixa imoveis={imoveis} mes={mes} ano={ano}
        onClose={()=>setModal(false)} onSaved={()=>{setModal(false);load();toast('Lançado!');}} />}
    </div>
  );
}

function ModalNovoCaixa({ imoveis, mes, ano, onClose, onSaved }) {
  const pad = n => String(n).padStart(2,'0');
  const dataD = `${ano}-${pad(mes)}-${pad(Math.min(new Date().getDate(), new Date(ano,mes,0).getDate()))}`;
  const [form, setForm] = useState({ data:dataD, socio:'PILO', tipo:'RECEBIMENTO', imovel_id:'', hospede_descricao:'', descricao:'', valor:'' });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      await api.adicionarCaixa({...form, imovel_id: form.imovel_id||null, valor: round2(form.valor)});
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title="Novo Lançamento" onClose={onClose}>
      <form onSubmit={salvar}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Sócio</label>
            <select className="form-input" value={form.socio} onChange={e=>f('socio',e.target.value)}>
              <option>PILO</option><option>BEIÇO</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tipo</label>
            <select className="form-input" value={form.tipo} onChange={e=>f('tipo',e.target.value)}>
              <option>RECEBIMENTO</option><option>GASTO BOLSO</option>
            </select>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Imóvel</label>
          <select className="form-input" value={form.imovel_id} onChange={e=>f('imovel_id',e.target.value)}>
            <option value="">Geral</option>
            {imoveis.map(i=><option key={i.id} value={i.id}>{i.nome}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Hóspede / Descrição</label>
          <input className="form-input" value={form.hospede_descricao} onChange={e=>f('hospede_descricao',e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Observação</label>
          <input className="form-input" value={form.descricao} onChange={e=>f('descricao',e.target.value)} />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Data</label>
            <input className="form-input" type="date" value={form.data} onChange={e=>f('data',e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Valor (R$)</label>
            <input className="form-input" type="number" step="0.01" value={form.valor} onChange={e=>f('valor',e.target.value)} required />
          </div>
        </div>
        <button className="btn btn-blue" disabled={load}>{load?<Spinner/>:'Salvar'}</button>
      </form>
    </Modal>
  );
}

// ─── REOCUPAÇÕES ──────────────────────────────────────────────────────────────
function Reocupacoes() {
  const { mes, ano, setMes, setAno } = useMesAno();
  const [imoveis, setImoveis] = useState([]);
  const [itens,   setItens]   = useState([]);
  const [modal,   setModal]   = useState(false);
  const toast = useToast();

  useEffect(() => { api.imoveis().then(setImoveis); }, []);
  const load = useCallback(() => api.reocupacoes(mes, ano).then(setItens), [mes, ano]);
  useEffect(() => { load(); }, [load]);

  async function excluir(id) {
    if (!window.confirm('Excluir?')) return;
    try { await api.excluirReocupacao(id); load(); toast('Excluído!'); }
    catch(e) { toast(e.message,'error'); }
  }

  const total = itens.reduce((s,r) => s+Number(r.valor), 0);

  return (
    <div>
      <TopBar title="🔄 Reocupações" />
      <div className="content">
        <MesPicker mes={mes} ano={ano} onChange={(m,a)=>{setMes(m);setAno(a);}} />
        <div className="flex justify-between items-center mb-8">
          <span style={{fontWeight:700,color:'var(--amber)',fontSize:16}}>{fmtBRL(total)}</span>
          <button className="btn btn-amber btn-sm" onClick={()=>setModal(true)}>+ Registrar</button>
        </div>
        {itens.length === 0
          ? <div className="empty-state"><div className="icon">🔄</div>Nenhuma reocupação este mês</div>
          : itens.map(r => (
            <div key={r.id} className="card" style={{padding:'12px 16px'}}>
              <div className="flex justify-between items-center">
                <div className="flex-1" style={{minWidth:0}}>
                  <div style={{fontWeight:600,fontSize:14}}>{r.imovel_nome} · {r.quarto||'?'}</div>
                  <div style={{fontSize:12,color:'var(--text-lt)'}}>
                    {r.hospede_anterior||'—'} → {r.hospede_novo||'—'}
                  </div>
                  <div style={{fontSize:11,color:'var(--text-lt)'}}>
                    Entrada: {new Date(r.data_entrada).toLocaleDateString('pt-BR')}
                    {r.observacao ? ' · ' + r.observacao : ''}
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <span style={{fontWeight:700,fontSize:15,color:'var(--amber)'}}>{fmtBRL(r.valor)}</span>
                  <button className="btn btn-danger btn-sm" style={{width:32,padding:'6px'}}
                    onClick={()=>excluir(r.id)}>✕</button>
                </div>
              </div>
            </div>
          ))}
      </div>
      {modal && <ModalNovaReocupacao imoveis={imoveis} mes={mes} ano={ano}
        onClose={()=>setModal(false)} onSaved={()=>{setModal(false);load();toast('Registrado!');}} />}
    </div>
  );
}

function ModalNovaReocupacao({ imoveis, mes, ano, onClose, onSaved }) {
  const pad = n => String(n).padStart(2,'0');
  const dataD = `${ano}-${pad(mes)}-${pad(Math.min(new Date().getDate(), new Date(ano,mes,0).getDate()))}`;
  const [form, setForm] = useState({ imovel_id:'', quarto:'', hospede_anterior:'', hospede_novo:'', valor:'', data_entrada:dataD, data_saida_prev:'', observacao:'' });
  const [load, setLoad] = useState(false);
  const toast = useToast();
  function f(k,v) { setForm(p=>({...p,[k]:v})); }

  async function salvar(e) {
    e.preventDefault(); setLoad(true);
    try {
      await api.adicionarReocupacao({...form, imovel_id: parseInt(form.imovel_id), valor: round2(form.valor)});
      onSaved();
    } catch(e) { toast(e.message,'error'); }
    finally { setLoad(false); }
  }

  return (
    <Modal title="Nova Reocupação" onClose={onClose}>
      <form onSubmit={salvar}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Imóvel</label>
            <select className="form-input" value={form.imovel_id} onChange={e=>f('imovel_id',e.target.value)} required>
              <option value="">Selecione</option>
              {imoveis.map(i=><option key={i.id} value={i.id}>{i.nome}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Quarto</label>
            <input className="form-input" value={form.quarto} onChange={e=>f('quarto',e.target.value)} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Hóspede Anterior</label>
          <input className="form-input" value={form.hospede_anterior} onChange={e=>f('hospede_anterior',e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Hóspede Novo</label>
          <input className="form-input" value={form.hospede_novo} onChange={e=>f('hospede_novo',e.target.value)} />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          <div className="form-group">
            <label className="form-label">Valor (R$)</label>
            <input className="form-input" type="number" step="0.01" value={form.valor} onChange={e=>f('valor',e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">Data Entrada</label>
            <input className="form-input" type="date" value={form.data_entrada} onChange={e=>f('data_entrada',e.target.value)} required />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Observação</label>
          <input className="form-input" value={form.observacao} onChange={e=>f('observacao',e.target.value)} />
        </div>
        <button className="btn btn-amber" disabled={load}>{load?<Spinner/>:'Salvar'}</button>
      </form>
    </Modal>
  );
}

// ─── BOTTOM NAV ───────────────────────────────────────────────────────────────
function BottomNav() {
  const loc = useLocation();
  const nav = useNavigate();
  const path = loc.pathname;

  const tabs = [
    { path:'/',          icon:'📊', label:'Dashboard' },
    { path:'/imoveis',  icon:'🏘', label:'Imóveis' },
    { path:'/gastos',   icon:'💸', label:'Gastos' },
    { path:'/caixa',    icon:'💰', label:'Caixa' },
    { path:'/reocu',    icon:'🔄', label:'Reocup.' },
  ];

  return (
    <nav className="bottom-nav">
      {tabs.map(t => (
        <button key={t.path}
          className={'nav-btn' + (path === t.path || (t.path !== '/' && path.startsWith(t.path)) ? ' active' : '')}
          onClick={() => nav(t.path)}>
          <span className="icon">{t.icon}</span>
          {t.label}
        </button>
      ))}
    </nav>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <div className="app">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/*" element={
              <ProtectedRoute>
                <div style={{display:'flex',flexDirection:'column',minHeight:'100dvh'}}>
                  <div style={{flex:1,overflowY:'auto'}}>
                    <Routes>
                      <Route path="/"           element={<Dashboard />} />
                      <Route path="/imoveis"    element={<Imoveis />} />
                      <Route path="/imovel/:id" element={<ImovelDetalhe />} />
                      <Route path="/gastos"     element={<Gastos />} />
                      <Route path="/caixa"      element={<Caixa />} />
                      <Route path="/reocu"      element={<Reocupacoes />} />
                    </Routes>
                  </div>
                  <BottomNav />
                </div>
              </ProtectedRoute>
            } />
          </Routes>
        </div>
      </ToastProvider>
    </BrowserRouter>
  );
}
